import { useMemo, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { format } from "date-fns";
import { toast } from "react-toastify";
import ModalDetails from "../../../components/modalDetails";
import { markSubscriptionResolved, sendToSalesforce } from "../services/api";

const STATUS_STYLES = {
  fulfilled: "bg-emerald-100 text-emerald-800 border-emerald-200",
  needs_attention: "bg-amber-100 text-amber-900 border-amber-300",
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  created: "bg-slate-100 text-slate-700 border-slate-200",
  rejected: "bg-rose-100 text-rose-800 border-rose-200",
  failed: "bg-rose-100 text-rose-800 border-rose-200",
  cancelled: "bg-rose-100 text-rose-800 border-rose-200",
};

function StatusBadge({ value }) {
  if (!value) return <span className="text-slate-400">—</span>;
  const lower = value.toLowerCase();
  const matchKey = Object.keys(STATUS_STYLES).find((key) => lower.includes(key));
  const style =
    STATUS_STYLES[matchKey] || "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-semibold uppercase tracking-wide whitespace-nowrap ${style}`}
    >
      {value}
    </span>
  );
}

function YesNoBadge({ value }) {
  const positive = !!value;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded border text-xs font-medium ${
        positive
          ? "bg-emerald-50 text-emerald-800 border-emerald-200"
          : "bg-slate-50 text-slate-600 border-slate-200"
      }`}
    >
      {positive ? "Sim" : "Não"}
    </span>
  );
}

function stringifyMessage(value, depth = 0) {
  if (depth > 6 || value == null) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (Array.isArray(value)) {
    const parts = value
      .map((item) => stringifyMessage(item, depth + 1))
      .filter(Boolean);
    return parts.length ? parts.join("; ") : null;
  }
  if (typeof value === "object") {
    return (
      stringifyMessage(value.message, depth + 1) ||
      stringifyMessage(value.reason, depth + 1) ||
      stringifyMessage(value.status, depth + 1) ||
      null
    );
  }
  return null;
}

function extractLastHistoryMessage(history) {
  if (!history || typeof history !== "string") return null;
  const trimmed = history.trim();
  if (!trimmed) return null;

  const tryParse = (str) => {
    try {
      return JSON.parse(str);
    } catch {
      return null;
    }
  };

  let entries = tryParse(`[${trimmed}]`);
  if (!Array.isArray(entries)) {
    const direct = tryParse(trimmed);
    if (Array.isArray(direct)) {
      entries = direct;
    } else if (direct && typeof direct === "object") {
      entries = [direct];
    }
  }
  if (!Array.isArray(entries) || entries.length === 0) return null;

  const last = entries[entries.length - 1];
  if (!last || typeof last !== "object") return null;

  return (
    stringifyMessage(last.listStatus?.message) ||
    stringifyMessage(last.message) ||
    null
  );
}

export default function SubscriptionRow({
  index,
  args,
  onFilterByRegister,
  onResolved,
}) {
  const [item, setItem] = useState(args);
  const [isLoading, setIsLoading] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [showModal, setShowModal] = useState(false);

  function isFulfilled(value) {
    return (value || "").toLowerCase().includes("fulfilled");
  }

  const lastMessage = useMemo(
    () => extractLastHistoryMessage(item.history),
    [item.history],
  );

  async function handleSendToSalesforce() {
    setIsLoading(true);
    let response;
    let statusCode;

    try {
      response = await sendToSalesforce(item.id);
      statusCode = JSON.parse(response).statusCode;
      if (statusCode === 200 || statusCode === 201) {
        toast.success("Subscription sent to Salesforce successfully!");
      } else {
        toast.error(
          "Failed to send subscription to Salesforce. Check details for more information."
        );
      }
    } catch (error) {
      console.error("Error sending subscription to Salesforce:", error);
      toast.error("An error occurred while sending the subscription.");
    } finally {
      if (response?.syncResponse) {
        setItem((prev) => ({
          ...prev,
          current_status: response.syncResponse.current_status,
          history: response.syncResponse.history,
          updated_at: response.syncResponse.updated_at,
        }));
      }
      setIsLoading(false);
    }
  }

  async function handleMarkResolved() {
    if (!window.confirm("Marcar esta inscrição como resolvida?")) return;
    setIsResolving(true);
    try {
      const response = await markSubscriptionResolved(item.id);
      if (response instanceof Error) {
        toast.error(response.message);
        return;
      }
      const updated = response.data;
      setItem((prev) => ({
        ...prev,
        current_status: updated?.current_status || prev.current_status,
        updated_at: updated?.updated_at || prev.updated_at,
      }));
      toast.success("Inscrição marcada como resolvida.");
      if (onResolved && updated) onResolved(updated);
    } catch (error) {
      console.error("Error marking subscription as resolved:", error);
      toast.error("Erro ao marcar como resolvida.");
    } finally {
      setIsResolving(false);
    }
  }

  const name = item.register?.name || "—";
  const email = item.register?.email || "—";
  const fulfilled = isFulfilled(item.current_status);

  return (
    <>
      <tr className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
        <td className="px-4 py-3 text-sm text-slate-400 tabular-nums">
          {index + 1}
        </td>
        <td className="px-4 py-3 text-sm text-slate-900 whitespace-nowrap">
          {name}
        </td>
        <td className="px-4 py-3 text-sm text-slate-900">
          {item.register_id && onFilterByRegister ? (
            <button
              type="button"
              className="text-blue-600 hover:text-blue-800 hover:underline bg-transparent border-0 p-0 text-left"
              title="Ver todas as inscrições deste registro"
              onClick={() => onFilterByRegister(item)}
            >
              {email}
            </button>
          ) : (
            email
          )}
        </td>
        <td className="px-4 py-3 text-sm">
          <YesNoBadge value={item.consent} />
        </td>
        <td className="px-4 py-3 text-sm">
          <YesNoBadge value={item.newsletter} />
        </td>
        <td className="px-4 py-3 text-sm font-mono font-semibold text-slate-700">
          {item.motivation_code_id || "—"}
        </td>
        <td className="px-4 py-3 text-sm font-mono font-semibold text-slate-700">
          {item.subscription_code_id || "—"}
        </td>
        <td className="px-4 py-3 text-sm">
          <div className="flex flex-col gap-1">
            <StatusBadge value={item.current_status} />
            {lastMessage && !fulfilled ? (
              <span
                className="text-[11px] text-slate-500 max-w-[280px] truncate"
                title={lastMessage}
              >
                {lastMessage}
              </span>
            ) : null}
          </div>
        </td>
        <td className="px-4 py-3 text-sm">
          <div className="flex flex-col gap-1">
            <Button
              size="sm"
              disabled={!item.history?.[0]}
              variant="outline-secondary"
              onClick={() => setShowModal(true)}
            >
              Details
            </Button>
            {!fulfilled && (
              <Button
                size="sm"
                variant="outline-success"
                disabled={isResolving}
                onClick={handleMarkResolved}
              >
                {isResolving ? <Spinner animation="border" size="sm" /> : "Resolver"}
              </Button>
            )}
          </div>
        </td>
        <td className="px-4 py-3 text-sm">
          {isLoading ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <Button
              size="sm"
              disabled={fulfilled}
              variant={fulfilled ? "success" : "outline-primary"}
              onClick={handleSendToSalesforce}
            >
              Sync
            </Button>
          )}
        </td>
        <td className="px-4 py-3 text-sm text-slate-500 tabular-nums whitespace-nowrap">
          {item.updated_at
            ? format(new Date(item.updated_at), "dd/MM/yyyy HH:mm")
            : "—"}
        </td>
      </tr>
      {showModal && (
        <ModalDetails
          show={showModal}
          onHide={() => setShowModal(false)}
          args={{
            modalTitle: "Subscription Details",
            buttonName: "Details",
            content: [item.history],
          }}
        />
      )}
    </>
  );
}
